import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";
import { ConfirmDialog } from "src/components/base/confirm-dialog";
import { notify } from "src/components/base/toast";

/**
 * The properties for {@link DeleteDeviceGroupDialog}
 */
export type DeleteDeviceGroupDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback for closing the popup */
    onClose: () => void;
    /** Callback for successful deletion */
    onDeletion: () => void;
    /** The device group */
    deviceGroup: SimpleDeviceGroup | undefined;
};

/**
 * A dialog to delete a device group. Surfaces blocking-reference errors
 * inline (devices / access-rules) instead of dismissing the dialog so the
 * user can resolve and retry.
 */
export default function DeleteDeviceGroupDialog(props: DeleteDeviceGroupDialogProps) {
    const [t] = useTranslation("dialog-delete-device-group");
    const [errors, setErrors] = React.useState<Array<string>>([]);

    React.useEffect(() => {
        if (!props.isOpen) setErrors([]);
    }, [props.isOpen]);

    if (!props.deviceGroup) return null;

    return (
        <ConfirmDialog
            open={props.isOpen}
            onClose={props.onClose}
            onConfirm={async () => {
                if (!props.deviceGroup) return;
                const res = await ApiInternal.deviceGroups.delete(props.deviceGroup.uuid);
                if (res.result === "Err") {
                    const next: Array<string> = [];
                    if (res.error.devices) {
                        next.push(t("error.devices-with-device-group", { count: res.error.devices }));
                    }
                    if (res.error.access_rules) {
                        next.push(t("error.access-rules-with-device-group", { count: res.error.access_rules }));
                    }
                    setErrors(next);
                    return;
                }
                notify.success(t("toast.device-group-deleted-successfully"));
                props.onDeletion();
            }}
            title={t("heading.delete-device-group", { deviceGroup: props.deviceGroup.name })}
            description={t("description.delete-device-group")}
            confirmLabel={t("button.delete-device-group")}
        >
            {errors.length > 0 && (
                <div className={"mt-3 flex flex-col gap-1 text-sm text-red-500 dark:text-red-400"}>
                    {errors.map((err, idx) => (
                        <span key={idx}>{err}</span>
                    ))}
                </div>
            )}
        </ConfirmDialog>
    );
}
